import { clientEnv } from "../env";

/**
 * Proving that the person at the keyboard still knows the current password.
 *
 * plan.md section 8 requires recent re-authentication before a sensitive action, and section 14
 * says why a modal dialog does not count. The mechanism that document specifies is the step-up
 * grant: a short-lived single-use row consumed by the same server function that performs the
 * action. That mechanism cannot reach a password change. A password change is performed by GoTrue,
 * not by a portal gateway, so there is no function of ours in the path to consume a grant in the
 * same transaction. Pretending otherwise by issuing a grant nobody consumes would be exactly the
 * replay window that section names.
 *
 * What is available is the password itself, checked against the same endpoint the login form posts
 * to. That is a real check rather than a dialog: it fails for somebody sitting at an unlocked
 * browser, which is the threat this control exists for.
 *
 * **It deliberately does not use the Supabase client.** `getSupabaseBrowserClient` is a singleton on
 * purpose, and its own comment says why: two clients would install two auth listeners and two
 * refresh timers against one stored session, and the losing one would keep serving a token the
 * other had already rotated. Signing in through it here would replace the caller's live session
 * with a new one, which for a Petrovic admin means silently dropping from `aal2` to `aal1` in the
 * middle of changing a password. Two plain requests hold no state and touch nothing.
 *
 * The session this creates is revoked immediately. `/logout?scope=local` ends the session belonging
 * to the token it is called with and no other, so the caller's own session on this device and any
 * session on their phone are untouched.
 *
 * ---
 *
 * **Two callers now, and one of them uses the token rather than throwing it away.** The password
 * change below only needs to know that the password was right. `app/auth/stepUp.ts` needs the
 * temporary session's own access token, because migration
 * `20260905160001_phase5e_step_up_wraps_the_actions_that_already_consume_it.sql` measures the `amr`
 * claim of the token the grant is requested with, and the primary session's `password` entry is
 * hours old by exactly that test. So the GoTrue exchange lives in `withTemporaryPasswordSession`
 * and both callers use it: one sign-in, one identity check, one local revoke, in one place.
 */

export type ReauthFailure =
  | "wrong-password"
  | "different-account"
  | "captcha"
  | "rate-limited"
  | "unavailable";

export type ReauthResult = { ok: true } | { ok: false; reason: ReauthFailure };

export const REAUTH_FAILURE_TEXT: Record<ReauthFailure, string> = {
  "wrong-password": "Nuvarande lösenord stämmer inte.",
  "different-account": "Uppgifterna hör till ett annat konto. Logga in igen och försök på nytt.",
  captcha: "Säkerhetskontrollen gick inte igenom. Ladda om sidan och försök igen.",
  "rate-limited": "För många försök på kort tid. Vänta en stund och försök igen.",
  unavailable: "Vi kunde inte kontrollera lösenordet just nu. Försök igen."
};

interface TokenBody {
  error_code?: unknown;
  error?: unknown;
  msg?: unknown;
  message?: unknown;
  access_token?: unknown;
  user?: unknown;
}

/**
 * GoTrue's shapes, read in the order they are trustworthy.
 *
 * `error_code` is the stable one and is what newer versions send. `error` is the OAuth-style field
 * older ones send. The message strings are last and are matched loosely, because they are prose and
 * prose changes. Anything unrecognised becomes `unavailable` rather than `wrong-password`: telling
 * somebody their password is wrong because the server returned something we did not expect is a
 * failure rendered as a fact.
 */
function classify(status: number, body: TokenBody): ReauthFailure {
  if (status === 429) {
    return "rate-limited";
  }

  const code = String(body.error_code ?? body.error ?? "");
  const text = String(body.msg ?? body.message ?? "").toLowerCase();

  if (code.includes("captcha") || text.includes("captcha")) {
    return "captcha";
  }
  if (code === "over_request_rate_limit" || code === "over_email_send_rate_limit") {
    return "rate-limited";
  }
  if (code === "invalid_credentials" || code === "invalid_grant" || text.includes("credentials")) {
    return "wrong-password";
  }

  return "unavailable";
}

export interface TemporaryPasswordSession {
  email: string;
  password: string;
  /** architecture.md section 4: "The returned user ID must equal the current user." */
  expectedUserId: string;
  captchaToken?: string;
}

export type TemporarySessionOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; reason: ReauthFailure };

/**
 * One temporary session, handed to `run`, and revoked before this function answers.
 *
 * The whole GoTrue contract architecture.md section 4 describes is here and nowhere else: the
 * sign-in against `grant_type=password` carrying the captcha token, the user-ID equality check, and
 * the local revoke. `run` receives the temporary session's access token and whatever it returns
 * comes back in `value`.
 *
 * **The revoke is in a `finally` and that is the point of the shape.** A caller that throws while
 * holding the token would otherwise leave a live session behind on every failure, which is a control
 * that accumulates the thing it protects. The identity check runs inside the same block, so a
 * password that proves a different account revokes the session it created before refusing.
 *
 * A failure to revoke is not a failure of the proof and is not reported as one. See `revoke`.
 *
 * The callback is called `run` rather than `use` because `react-hooks/rules-of-hooks` reads a call
 * to `use(...)` as React's own `use` hook and refuses it inside a `try`, which is exactly where this
 * one has to be.
 */
export async function withTemporaryPasswordSession<T>(
  input: TemporaryPasswordSession,
  run: (accessToken: string) => Promise<T>
): Promise<TemporarySessionOutcome<T>> {
  const url = clientEnv.VITE_SUPABASE_URL;
  const key = clientEnv.VITE_SUPABASE_PUBLISHABLE_KEY;

  if (!url || !key) {
    return { ok: false, reason: "unavailable" };
  }

  let response: Response;
  let body: TokenBody;

  try {
    response = await fetch(`${url}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { apikey: key, "Content-Type": "application/json" },
      body: JSON.stringify({
        email: input.email,
        password: input.password,
        ...(input.captchaToken
          ? { gotrue_meta_security: { captcha_token: input.captchaToken } }
          : {})
      })
    });
    body = (await response.json()) as TokenBody;
  } catch {
    return { ok: false, reason: "unavailable" };
  }

  if (!response.ok) {
    return { ok: false, reason: classify(response.status, body) };
  }

  const accessToken = typeof body.access_token === "string" ? body.access_token : null;
  if (!accessToken) {
    // A 200 with no token is not a password that checked out. It is a response we do not understand.
    return { ok: false, reason: "unavailable" };
  }

  try {
    /**
     * The account that answered has to be the account that asked.
     *
     * architecture.md section 4 names this check as part of the contract. The email is taken from
     * the caller's own session rather than from a field they can type in, so this is a second lock
     * on a door that is already shut. It is still worth having, because the day somebody adds an
     * email field to one of these forms the check is what stops a correct password for a different
     * account from counting as proof for this one.
     */
    const proved = (body.user as { id?: unknown } | undefined)?.id;
    if (typeof proved !== "string" || proved !== input.expectedUserId) {
      return { ok: false, reason: "different-account" };
    }

    return { ok: true, value: await run(accessToken) };
  } finally {
    await revoke(url, key, accessToken);
  }
}

/**
 * The password change's own use of the exchange above: it needs the answer and not the token.
 *
 * The signature, the failure names and the order of the two requests are unchanged from when this
 * function held the whole exchange itself, because `SettingsRoute` and `tests/reauthenticate.test.ts`
 * both depend on them.
 */
export async function verifyCurrentPassword(
  email: string,
  password: string,
  expectedUserId: string,
  captchaToken?: string
): Promise<ReauthResult> {
  const outcome = await withTemporaryPasswordSession(
    { email, password, expectedUserId, captchaToken },
    async () => undefined
  );

  return outcome.ok ? { ok: true } : { ok: false, reason: outcome.reason };
}

/**
 * Ends the session this proof created, and only that one.
 *
 * `scope=local` rather than the SDK's default of `global`. architecture.md section 4 measured both
 * against the staging project on 2026-08-30: the local scope returns 204, the primary session
 * survives it and the temporary session's refresh token is dead afterwards, while a global sign-out
 * would revoke every session the person has anywhere. Ending somebody's session on their phone
 * because they typed their own password on their laptop is the opposite of what this is for.
 *
 * A failure here is not a failure of the check and is not reported as one. The token expires on its
 * own, and telling the caller their password was wrong because a cleanup request did not land would
 * be false about the thing that was actually asked.
 */
async function revoke(url: string, key: string, accessToken: string): Promise<void> {
  try {
    await fetch(`${url}/auth/v1/logout?scope=local`, {
      method: "POST",
      headers: { apikey: key, Authorization: `Bearer ${accessToken}` }
    });
  } catch {
    // Intentionally ignored. See above.
  }
}
